import { Grid, IconButton, Typography } from '@mui/material';
import { memo, useCallback, useState } from 'react';
import {
  CommonCivilStatusFormVM,
  ListTableItemVM,
} from 'civil-status/view-models/document-birth-form';
import { PageMode } from 'civil-status/view-models/page-mode';
import { HeadCell } from 'shell/components/shared/table/types';
import Table from 'shell/components/shared/table';
import { noop } from 'shell/utils/noop';
import CardAnnotationForm from './card-annotation';
import { toast } from 'react-toastify';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import DeleteAlertModal from 'system/components/shared/delete-alert';
import DeleteSuccessModal from 'system/components/shared/delete-success';
import { AddCircleOutline } from '@mui/icons-material';
import { Annotation } from 'shell/models/annotation';
import { HttpResponse } from 'shell/models/http-response';
import { useListener } from 'shell/hooks/use-bus';
import { DeleteAllEvent } from 'shell/components/shared/certification-civil-status/certification-form/document-visualization/buttons';

interface AnnotationFormProps {
  values?: CommonCivilStatusFormVM;
  onChange: (
    key: keyof CommonCivilStatusFormVM,
    value: CommonCivilStatusFormVM[keyof CommonCivilStatusFormVM]
  ) => void;
  mode: PageMode;
  setMode: (mode: PageMode) => void;
  type: string;
}

const headCells: HeadCell<ListTableItemVM, keyof ListTableItemVM>[] = [
  // {
  //   id: 'id',
  //   label: 'Codice',
  // },
  {
    id: 'value',
    label: 'Annotazioni',
  },
  {
    id: 'action',
    label: '',
  },
];

const AnnotationComponent = ({
  values,
  mode,
  type,
  onChange,
  setMode,
}: AnnotationFormProps): React.ReactElement => {
  const editAnnotationInformation = useHttpClient(`${__CIVIL_STATUS_API_URL__}/annotazioni-atto`, {
    options: {
      method: 'POST',
      loading: true,
      errorCatching: true,
    },
  });

  const deleteAnnotation = useHttpClient(`${__CIVIL_STATUS_API_URL__}/annotazioni-atto/rimuovi`, {
    options: {
      method: 'DELETE',
      loading: true,
      errorCatching: true,
    },
  });

  const [pageNational, setPageNational] = useState({ page: 0, rowsPerPage: 10 });

  const [cardAnnotationState, setCardAnnotationState] = useState<{
    open: boolean;
    item: ListTableItemVM | null;
    isNaz: boolean;
  }>({
    open: false,
    item: null,
    isNaz: false,
  });

  const [deleteModalState, setDeleteModalState] = useState<{
    open: boolean;
    item: ListTableItemVM | null;
  }>({
    open: false,
    item: null,
  });

  const [deleteAllModalState, setDeleteAllModalState] = useState<boolean>();

  const [deleteSuccessModal, setDeleteSuccessModalState] = useState<{
    open: boolean;
    item: ListTableItemVM | null;
  }>({
    open: false,
    item: null,
  });

  useListener(DeleteAllEvent, () => {
    setDeleteAllModalState(true);
  });

  const onChangeNationalPage = useCallback(
    (page) => {
      setPageNational((oldState) => ({ ...oldState, page }));
    },
    [setPageNational]
  );

  const onChangeNationalRowsPerPage = useCallback(
    (rowsPerPage) => {
      setPageNational((oldState) => ({ ...oldState, rowsPerPage }));
    },
    [setPageNational]
  );

  const onEditPress = useCallback(
    (cell) => {
      setCardAnnotationState({ open: true, item: cell, isNaz: false });
    },
    [setCardAnnotationState]
  );

  const onEditPressNaz = useCallback(
    (cell) => {
      setCardAnnotationState({ open: true, item: cell, isNaz: true });
    },
    [setCardAnnotationState]
  );

  const onAddPress = useCallback(() => {
    setCardAnnotationState({ open: true, item: null, isNaz: false });
  }, [setCardAnnotationState]);

  const onAddPressNaz = useCallback(() => {
    setCardAnnotationState({ open: true, item: null, isNaz: true });
  }, [setCardAnnotationState]);

  const onTrashPress = useCallback(
    (cell) => {
      setDeleteModalState({ open: true, item: cell });
    },
    [setDeleteModalState]
  );

  const [pageInternational, setPageInternational] = useState({ page: 0, rowsPerPage: 10 });

  const onChangeInternationalPage = useCallback(
    (page) => {
      setPageInternational((oldState) => ({ ...oldState, page }));
    },
    [setPageInternational]
  );

  const onChangeInternationalRowsPerPage = useCallback(
    (rowsPerPage) => {
      setPageInternational((oldState) => ({ ...oldState, rowsPerPage }));
    },
    [setPageInternational]
  );

  const deleteAll = useHttpClient(`${__CIVIL_STATUS_API_URL__}/annotazioni-atto/rimuovi/all`, {
    options: {
      method: 'DELETE',
      loading: true,
      errorCatching: true,
    },
  });

  const handleDeleteAllInternational = useCallback(() => {
    (
      deleteAll({
        data: {
          key: {
            tipoAtto: type,
            numeroRegistro: values?.registerNumber,
            anno: values?.year,
            numeroAtto: values?.documentNumber,
            parte: values?.portion,
            serie1: `${values?.serie || ''}${values?.serie2 || ''}`,
          },
          tipoCertificato: 'INT',
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione eseguita con successo');
      onChange('annotationInternationalList', []);
      setDeleteAllModalState(false);
      setMode(PageMode.Detail);
    });
  }, [deleteAll, setMode, values, type, setDeleteAllModalState]);

  const handleDeleteAllNational = useCallback(() => {
    (
      deleteAll({
        data: {
          key: {
            tipoAtto: type,
            numeroRegistro: values?.registerNumber,
            anno: values?.year,
            numeroAtto: values?.documentNumber,
            parte: values?.portion,
            serie1: `${values?.serie || ''}${values?.serie2 || ''}`,
          },
          tipoCertificato: 'NAZ',
        },
      }) as ResponsePromise<void>
    ).then(() => {
      onChange('annotationNationalList', []);
      toast.success('Operazione eseguita con successo');
      setDeleteAllModalState(false);
      setMode(PageMode.Detail);
    });
  }, [values, type, deleteAll, setMode, setDeleteAllModalState]);

  return (
    <>
      {mode === PageMode.National ? (
        <>
          <Grid item xs={12} display="flex" justifyContent="flex-end">
            <span>
              <IconButton
                onClick={onAddPressNaz}
                aria-label="Aggiungi annotazione"
                aria-haspopup="true"
              >
                <AddCircleOutline />
              </IconButton>
              <Typography color="secondary" display="inline-block">
                Aggiungi annotazione
              </Typography>
            </span>
          </Grid>
          <Grid item xs={12}>
            <Table
              ariaLabel="Lista annotazioni nazionali"
              page={pageNational.page}
              rowsPerPage={pageNational.rowsPerPage}
              body={values?.annotationNationalList || []}
              count={values?.annotationNationalList?.length || 0}
              head={headCells}
              onSort={noop}
              onPageChange={onChangeNationalPage}
              onRowsPerPageChange={onChangeNationalRowsPerPage}
              onVisibilityPress={noop}
              onEditPress={onEditPressNaz}
              onTrashPress={onTrashPress}
              orders={{}}
              isNotLayoutFixed
              noSort
              minWidth={0}
            />
          </Grid>
          {cardAnnotationState.open && cardAnnotationState.isNaz ? (
            <CardAnnotationForm
              type="NAZ"
              isEdit={!!cardAnnotationState.item}
              initialValues={
                cardAnnotationState.item
                  ? {
                      note: cardAnnotationState.item.value,
                      annotation: '',
                    }
                  : null
              }
              onClose={() => {
                setCardAnnotationState({ open: false, item: null, isNaz: false });
              }}
              onSubmit={(formValues) => {
                (
                  editAnnotationInformation({
                    data: {
                      key: {
                        tipoAtto: type,
                        numeroRegistro: values?.registerNumber,
                        anno: values?.year,
                        numeroAtto: values?.documentNumber,
                        parte: values?.portion,
                        serie1: `${values?.serie || ''}${values?.serie2 || ''}`,
                        posizione: cardAnnotationState.item?.position || undefined,
                      },
                      tipoCertificato: 'NAZ',
                      testo: formValues.note,
                    },
                    url: !!cardAnnotationState.item ? '/modifica' : '/create',
                    method: !!cardAnnotationState.item ? 'PUT' : 'POST',
                  }) as ResponsePromise<HttpResponse<Annotation>>
                ).then((response) => {
                  toast.success('Operazione eseguita con successo');
                  let mappedValues = values?.annotationNationalList?.slice() || [];

                  const foundElement = values?.annotationNationalList?.find(
                    (annotation) => annotation.id === cardAnnotationState.item?.id
                  );

                  if (foundElement) {
                    mappedValues = mappedValues.map((annotation) =>
                      annotation.id === cardAnnotationState.item?.id
                        ? {
                            ...annotation,
                            value: formValues.note,
                          }
                        : annotation
                    );
                  } else {
                    mappedValues.push({
                      id: mappedValues.length + 1,
                      value: formValues.note,
                      position: response?.data?.payload?.key?.posizione,
                      hidden: {
                        read: true,
                      },
                    });
                  }
                  onChange('annotationNationalList', mappedValues);
                  setCardAnnotationState({ open: false, item: null, isNaz: false });
                });
              }}
            />
          ) : undefined}
        </>
      ) : undefined}
      {mode === PageMode.International ? (
        <>
          <Grid item xs={12} display="flex" justifyContent="flex-end">
            <span>
              <IconButton
                onClick={onAddPress}
                aria-label="Aggiungi annotazione"
                aria-haspopup="true"
              >
                <AddCircleOutline />
              </IconButton>
              <Typography color="secondary" display="inline-block">
                Aggiungi annotazione
              </Typography>
            </span>
          </Grid>
          <Grid item xs={12}>
            <Table
              ariaLabel="Lista annotazioni internazionali"
              page={pageInternational.page}
              rowsPerPage={pageInternational.rowsPerPage}
              body={values?.annotationInternationalList || []}
              count={values?.annotationInternationalList?.length || 0}
              head={headCells}
              onSort={noop}
              onPageChange={onChangeInternationalPage}
              onRowsPerPageChange={onChangeInternationalRowsPerPage}
              onVisibilityPress={noop}
              onEditPress={onEditPress}
              onTrashPress={onTrashPress}
              orders={{}}
              isNotLayoutFixed
              noSort
              minWidth={0}
            />
          </Grid>
          {cardAnnotationState.open && !cardAnnotationState.isNaz ? (
            <CardAnnotationForm
              type="INT"
              isEdit={!!cardAnnotationState.item}
              initialValues={
                cardAnnotationState.item
                  ? {
                      note: cardAnnotationState.item.value,
                      annotation: '',
                    }
                  : null
              }
              onClose={() => {
                setCardAnnotationState({ open: false, item: null, isNaz: false });
              }}
              onSubmit={(formValues) => {
                (
                  editAnnotationInformation({
                    data: {
                      key: {
                        tipoAtto: type,
                        numeroRegistro: values?.registerNumber,
                        anno: values?.year,
                        numeroAtto: values?.documentNumber,
                        parte: values?.portion,
                        serie1: `${values?.serie || ''}${values?.serie2 || ''}`,
                        posizione: cardAnnotationState.item?.position || undefined,
                      },
                      tipoCertificato: 'INT',
                      testo: formValues.note,
                    },
                    url: !!cardAnnotationState.item ? '/modifica' : '/create',
                    method: !!cardAnnotationState.item ? 'PUT' : 'POST',
                  }) as ResponsePromise<HttpResponse<Annotation>>
                ).then((response) => {
                  toast.success('Operazione eseguita con successo');
                  const foundElement = values?.annotationInternationalList?.find(
                    (annotation) => annotation.id === cardAnnotationState.item?.id
                  );
                  let mappedValues = values?.annotationInternationalList?.slice() || [];

                  if (foundElement) {
                    mappedValues = mappedValues.map((annotation) =>
                      annotation.id === cardAnnotationState.item?.id
                        ? {
                            ...annotation,
                            value: formValues.note,
                          }
                        : annotation
                    );
                  } else {
                    mappedValues.push({
                      id: mappedValues.length + 1,
                      position: response?.data?.payload?.key?.posizione,
                      value: formValues.note,
                      hidden: {
                        read: true,
                      },
                    });
                  }

                  onChange('annotationInternationalList', mappedValues);
                  setCardAnnotationState({ open: false, item: null, isNaz: false });
                });
              }}
            />
          ) : undefined}
        </>
      ) : undefined}
      <DeleteAlertModal
        title="Cancellazione Annotazioni"
        message="Confermare la cancellazione?"
        open={deleteAllModalState || false}
        onClose={() => setDeleteAllModalState(false)}
        onYes={() => {
          if (mode === PageMode.International) {
            handleDeleteAllInternational();
          }

          if (mode === PageMode.National) {
            handleDeleteAllNational();
          }
        }}
        onNo={() => setDeleteAllModalState(false)}
      />
      <DeleteAlertModal
        title="Cancellazione Annotazione"
        message="Confermare la cancellazione?"
        open={deleteModalState.open}
        onClose={() => setDeleteModalState({ open: false, item: null })}
        onYes={() => {
          (
            deleteAnnotation({
              data: {
                key: {
                  tipoAtto: type,
                  numeroRegistro: values?.registerNumber,
                  anno: values?.year,
                  numeroAtto: values?.documentNumber,
                  parte: values?.portion,
                  serie1: `${values?.serie || ''}${values?.serie2 || ''}`,
                  posizione: deleteModalState.item?.position,
                },
              },
            }) as ResponsePromise<void>
          ).then(() => {
            let mappedValues =
              (mode === PageMode.National
                ? values?.annotationNationalList?.slice()
                : values?.annotationInternationalList?.slice()) || [];

            mappedValues = mappedValues.filter((item) => item.id !== deleteModalState.item?.id);

            onChange(
              mode === PageMode.National ? 'annotationNationalList' : 'annotationInternationalList',
              mappedValues
            );
            setDeleteSuccessModalState({ open: true, item: deleteModalState.item });
            setDeleteModalState({ open: false, item: null });
          });
        }}
        onNo={() => setDeleteModalState({ open: false, item: null })}
      />
      <DeleteSuccessModal
        open={deleteSuccessModal.open}
        title="Cancellazione Annotazione"
        message={`La cancellazione è avvenuta con successo.`}
        onClose={() => {
          setDeleteSuccessModalState({ open: false, item: null });
        }}
        onOk={() => {
          setDeleteSuccessModalState({ open: false, item: null });
        }}
      />
    </>
  );
};

AnnotationComponent.displayName = 'Annotation form component';

const AnnotationForm = memo(AnnotationComponent);

export default AnnotationForm;
